import { Feature, FeatureCollection, LineString, Point } from "geojson";
import { Layer, Source } from "react-map-gl";
import { makeTaskRenderer } from "../TaskLayers";
import { MapImageId } from "../types";
import { point } from "@turf/helpers";
import { TASK_BASE_PROPS, TaskFeatureProperties } from "common/utils/rtcJobs";
import React, { useMemo } from "react";
import transformTranslate from "@turf/transform-translate";

enum LayerIds {
  SYMBOL = "g2t-symbol",
  APPROACH_LINES = "g2t-approach-lines",
}

export const renderGoToAndFaceTask = makeTaskRenderer({
  detailsField: "goToAndFace",
  Render: ({ tasks, extraLayerProps, beforeId }) => {
    const data: {
      points: FeatureCollection<Point, TaskFeatureProperties>;
      approachLines: FeatureCollection<LineString, TaskFeatureProperties>;
    } = useMemo(() => {
      const points: FeatureCollection<Point, TaskFeatureProperties> = {
        type: "FeatureCollection",
        features: [],
      };

      const approachLines: FeatureCollection<
        LineString,
        TaskFeatureProperties
      > = {
        type: "FeatureCollection",
        features: [],
      };

      for (const task of tasks) {
        const taskData = task.goToAndFace;

        if (taskData.point && taskData.heading) {
          const taskPoint = point([taskData.point.lng, taskData.point.lat]);
          const behindPoint: Feature<Point> = transformTranslate(
            taskPoint,
            -50,
            taskData.heading,
            { units: "meters" }
          );

          points.features.push({
            type: "Feature",
            geometry: taskPoint.geometry,
            properties: {
              ...TASK_BASE_PROPS,
              task,
            },
          });

          approachLines.features.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: [
                behindPoint.geometry.coordinates,
                taskPoint.geometry.coordinates,
              ],
            },
            properties: {
              ...TASK_BASE_PROPS,
              task,
            },
          });
        }
      }

      return { points, approachLines };
    }, [tasks]);

    return (
      <>
        <Source data={data.points} type="geojson">
          <Layer
            id={LayerIds.SYMBOL}
            beforeId={beforeId}
            type="symbol"
            layout={{
              "icon-image": MapImageId.GoToAndFace,
              "icon-rotate": [
                "get",
                "heading",
                ["get", "goToAndFace", ["get", "task"]],
              ],
              "icon-rotation-alignment": "map",
            }}
            {...extraLayerProps}
          />
        </Source>
        <Source data={data.approachLines} type="geojson">
          <Layer
            id={LayerIds.APPROACH_LINES}
            beforeId={LayerIds.SYMBOL}
            type="line"
            paint={{
              "line-dasharray": [2, 2],
            }}
            {...extraLayerProps}
          />
        </Source>
      </>
    );
  },
  interactiveLayerIds: [LayerIds.SYMBOL],
  imagesToLoad: [MapImageId.GoToAndFace],
});
